import { Button, Col, DatePicker, Divider, Form, Input, Row, Select, Space } from "antd";
import { useState } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import PermissionCheck from "../../../Common/PermissionCheck";
import { useSeasonService } from "~pages/Seasons/hooks/useSeasonService.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { Genders } from "~enums/genders";
import { Representations } from "~enums/representations";
import { SelectLeaderCriteria } from "~components/SelectLeaderCriteria/SelectLeaderCriteria.js";
import { SelectStandingConfiguration } from "~components/SelectStandingsConfiguration/SelectStandingConfiguration.js";
import { SelectFixtureProfile } from "~components/SelectFixtureProfile/SelectFixtureProfile.js";
import { normalizeFormFieldValue } from "~utilities/normalizeFormFieldValue.js";
import { noLeadingOrTrailingSpacesRule } from "~utils/noLeadingOrTrailingSpacesRule.ts";

const PermissionName = PermissionNames.SEASON;

const STATUS = ["ACTIVE", "COMPLETE", "DRAFT", "INACTIVE", "PENDING"];
const GENDER = [Genders.Male, Genders.Female, Genders.Mixed, Genders.Unknown]
const REPRESENTATION = [Representations.Club, Representations.Country, Representations.Person, Representations.Region, Representations.State];

const YEAR = () => {
  const yearList = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear - 5; i <= currentYear + 5; i++) {
    yearList.push(i);
  }
  return yearList;
};

const NewSeasonForm = () => {
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();
  const { sport, organizationId } = useParams();
  const { seasonStandards: STANDARDS, ageGroups: AGE_GROUP, disciplines: DISCIPLINE, seasonTypes, uiConfiguration } = useSportConfiguration();
  const {seasonUpsert: seasonUpsertUiConfiguration} = uiConfiguration.pages;

  const [previewIdList, setPreviewIdList] = useState({});
  const { createSeason, isCreatePending } = useSeasonService();

  const handleSend = async () => {
    const formData = await formatFormData();
    await createSeason(formData);
  };

  const formatFormData = async () => {
    const formData = await form.validateFields();
    formData.profileId = formData.profileId?.value;
    formData.standingConfigurationId = formData.standingConfigurationId?.value;
    formData.leaderCriteriaId = formData.leaderCriteriaId?.value;
    return normalizeFormFieldValue(formData);
  };

  return (
    <Space direction="vertical" size={16}>
      <Form
        form={form}
        layout={"inline"}
        labelCol={{ span: 11 }}
        wrapperCol={{ span: 13 }}
        colon={false}
        initialValues={{
          includeInStatistics: true,
          videoProduction: "NONE",
          discipline: seasonUpsertUiConfiguration.disciplines.defaultDiscipline,
          rosterConfiguration: {
            fixtureRosterLock: false,
            seasonRosterLock: false,
            windowCloseDate: null,
            windowOpenDate: null,
          },
          status: "DRAFT"
        }}
      >
        <Col span={6}>
          <Form.Item
            name={"nameLocal"}
            label={formatMessage({
              id: "nameLocal",
              defaultMessage: "Local Name",
            })}
            tooltip={formatMessage(
              {
                id: "form.help.local.name.item",
                defaultMessage: "The name of the {item} in the local Language",
              },
              {
                item: formatMessage({
                  id: "season",
                  defaultMessage: "Season",
                }),
              },
            )}
            required
            rules={[
              { required: true },
              {
                min: 2,
                message: formatMessage({ id: "form.validation.min.length" }, { length: 2 }),
              },
              noLeadingOrTrailingSpacesRule(formatMessage),
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"nameLatin"}
            label={formatMessage({
              id: "nameLatin",
              defaultMessage: "Latin Name",
            })}
            tooltip={formatMessage(
              {
                id: "form.help.latin.name.item",
                defaultMessage: "The name of the {item} in latin characters",
              },
              {
                item: formatMessage({
                  id: "season",
                  defaultMessage: "Season",
                }),
              },
            )}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"nameShortLocal"}
            label={formatMessage({
              id: "name.local.abbreviation",
              defaultMessage: "Local Abbr",
            })}
            tooltip={formatMessage({
              id: "form.help.local.abbreviation",
              defaultMessage: "An abbreviation/short name in the local language",
            })}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"nameShortLatin"}
            label={formatMessage({
              id: "name.latin.abbreviation",
              defaultMessage: "Latin Abbr",
            })}
            tooltip={formatMessage({
              id: "form.help.latin.abbreviation",
              defaultMessage: "An abbreviation/short name in latin characters",
            })}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"year"}
            label={formatMessage({
              id: "year",
              defaultMessage: "Year",
            })}
            required
            rules={[{ required: true }]}
            tooltip={formatMessage({
              id: "form.help.season.year",
              defaultMessage: "Year of the season",
            })}
          >
            <Select showSearch>
              {YEAR().map((year) => (
                <Select.Option value={year} key={year}>
                  {year}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"seasonType"}
            label={formatMessage({
              id: "season.type",
              defaultMessage: "Season Type",
            })}
            tooltip={formatMessage({
              id: "form.help.season.type",
              defaultMessage: "The type of matches added to this season",
            })}
            required
            rules={[{ required: true }]}
          >
            <Select options={[...seasonTypes.map(type => ({value: type, label: type}))]} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"status"}
            label={formatMessage({
              id: "status",
              defaultMessage: "Status",
            })}
            tooltip={formatMessage(
              {
                id: "form.help.status.item",
                defaultMessage: "What is the current status of this {item}?",
              },
              {
                item: formatMessage({
                  id: "season",
                  defaultMessage: "Season",
                }),
              },
            )}
            required
            rules={[{ required: true }]}
          >
            <Select>
              {STATUS?.map((status) => (
                <Select.Option value={status} key={status}>
                  {status}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"startDate"}
            label={formatMessage({
              id: "date.start",
              defaultMessage: "Start Date",
            })}
            tooltip={formatMessage({
              id: "form.help.season.start.date",
              defaultMessage: "Season start date",
            })}
            required
            rules={[{ required: true }]}
          >
            <DatePicker allowClear={false} size={"middle"} format={"DD/MM/YYYY"} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"endDate"}
            label={formatMessage({
              id: "date.end",
              defaultMessage: "End Date",
            })}
            tooltip={formatMessage({
              id: "form.help.season.end.date",
              defaultMessage: "Season end date",
            })}
          >
            <DatePicker size={"middle"} format={"DD/MM/YYYY"} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"gender"}
            label={formatMessage({
              id: "gender",
              defaultMessage: "Gender",
            })}
            tooltip={formatMessage({
              id: "form.help.season.gender",
              defaultMessage: "The gender of the participants in the season",
            })}
            required
            rules={[{ required: true }]}
          >
            <Select>
              {GENDER?.map((gender) => (
                <Select.Option value={gender} key={gender}>
                  {gender}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"ageGroup"}
            label={formatMessage({
              id: "ageGroup",
              defaultMessage: "Age Group",
            })}
            tooltip={formatMessage({
              id: "form.help.season.age.group",
              defaultMessage: "The age group of the season",
            })}
            required
            rules={[{ required: true }]}
          >
            <Select showSearch>
              {AGE_GROUP?.map((ageGroup) => (
                <Select.Option value={ageGroup} key={ageGroup}>
                  {ageGroup}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"standard"}
            label={formatMessage({
              id: "standard",
              defaultMessage: "Standard",
            })}
            tooltip={formatMessage({
              id: "form.help.season.standard",
              defaultMessage: "The playing standard of the season",
            })}
            required
            rules={[{ required: true }]}
          >
            <Select showSearch>
              {STANDARDS?.map((standard) => (
                <Select.Option value={standard} key={standard}>
                  {standard}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"representation"}
            label={formatMessage({
              id: "representation",
              defaultMessage: "Representation",
            })}
            tooltip={formatMessage({
              id: "form.help.season.representation",
              defaultMessage: "What level are the competitors representing",
            })}
            required
            rules={[{ required: true }]}
          >
            <Select showSearch>
              {REPRESENTATION?.map((representation) => (
                <Select.Option value={representation} key={representation}>
                  {representation}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"standingConfigurationId"}
            label={formatMessage({
              id: "standings.configuration",
              defaultMessage: "Standings Configuration",
            })}
            tooltip={formatMessage({
              id: "form.help.season.standings.configuration",
              defaultMessage: "What standings configuration belongs to this season?",
            })}
          >
            <Form.Item name={"standingConfigurationId"} style={{ width: "100%" }}>
              <SelectStandingConfiguration onChange={(value) =>
                setPreviewIdList((prevState) => ({...prevState, standingConfigurationId: value?.value}))}
              />
            </Form.Item>
            {previewIdList?.standingConfigurationId && (
              <div>
                <Link
                  target={"_blank"}
                  to={`/${customerId}/admin/${sport}/${organizationId}/standings-configuration/manage/${previewIdList?.standingConfigurationId}`}
                >
                  {formatMessage({
                    id: "view.configuration",
                    defaultMessage: "View Configuration",
                  })}
                </Link>
              </div>
            )}
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"profileId"}
            label={formatMessage({
              id: "match.profile",
              defaultMessage: "Match Profile",
            })}
            tooltip={formatMessage({
              id: "form.help.season.match.profile",
              defaultMessage: "What match profile belongs to this season?",
            })}
          >
            <Form.Item name={"profileId"} style={{ width: "100%" }}>
              <SelectFixtureProfile onChange={(value) =>
                setPreviewIdList((prevState) => ({...prevState, profileId: value?.value}))}
              />
            </Form.Item>
            {previewIdList?.profileId && (
              <Link
                target={"_blank"}
                to={`/${customerId}/admin/${sport}/${organizationId}/match-profiles/manage/${previewIdList?.profileId}`}
              >
                {formatMessage({
                  id: "view.configuration",
                  defaultMessage: "View Configuration",
                })}
              </Link>
            )}
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"leaderCriteriaId"}
            label={formatMessage({
              id: "leaders.qualification",
              defaultMessage: "Leaders Qualification",
            })}
            tooltip={formatMessage({
              id: "form.help.season.leaders.qualification",
              defaultMessage: "What leader qualification belongs to this season?",
            })}
          >
            <Form.Item name={"leaderCriteriaId"} style={{ width: "100%" }}>
                <SelectLeaderCriteria />
            </Form.Item>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"externalId"}
            label={formatMessage({
              id: "externalId",
              defaultMessage: "External Id",
            })}
            tooltip={formatMessage({
              id: "externalId",
              defaultMessage: "External Id",
            })}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"lockStandings"}
            label={formatMessage({
              id: "standings.lock",
              defaultMessage: "Lock Standings",
            })}
            tooltip={formatMessage({
              id: "form.help.season.lock.standings",
              defaultMessage: "Is the standings generation locked?",
            })}
            required
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value={true}>Yes</Select.Option>
              <Select.Option value={false}>No</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"includeInStatistics"}
            label={formatMessage({
              id: "includeInStatistics",
              defaultMessage: "Include in statistics",
            })}
            tooltip={formatMessage({
              id: "form.help.season.include.in.statistics",
              defaultMessage: "Include this season in calculated statistics?",
            })}
          >
            <Select>
              <Select.Option value={true}>Yes</Select.Option>
              <Select.Option value={false}>No</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={"videoProduction"}
            label={formatMessage({
              id: "video.production",
              defaultMessage: "Video Production",
            })}
            tooltip={formatMessage({
              id: "form.help.season.video.production",
              defaultMessage: "Data synchronization strategy with video production systems",
            })}
          >
            <Select>
              <Select.Option value={"NONE"}>None</Select.Option>
              <Select.Option value={"AUTOMATED"}>Automated</Select.Option>
              <Select.Option value={"MANUAL"}>Manual</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        {seasonUpsertUiConfiguration.disciplines.enabled && (
          <Col span={6}>
            <Form.Item
              name={"discipline"}
              label={formatMessage({
                id: "discipline",
                defaultMessage: "Discipline",
              })}
              tooltip={formatMessage({
                id: "form.help.season.discipline",
                defaultMessage: "What type of discipline will this season be?",
              })}
              required
              rules={[{ required: true }]}
            >
              <Select showSearch>
                {seasonUpsertUiConfiguration.disciplines.isNullable && (
                  <Select.Option value={"NONE"}>--</Select.Option>
                )}
                {DISCIPLINE?.map((discipline) => (
                  <Select.Option value={discipline} key={discipline}>
                    {discipline}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}

        <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
          <Divider plain>
            {formatMessage({
              id: "roster.related",
              defaultMessage: "Roster Related",
            })}
          </Divider>
          <Col span={6}>
            <Form.Item
              name={["rosterConfiguration", "windowOpenDate"]}
              label={formatMessage({
                id: "season.window.open.date",
                defaultMessage: "Window Open Date",
              })}
              tooltip={formatMessage({
                id: "form.help.season.window.open.date",
                defaultMessage: "What date can a Season Roster be submitted from for this Season?",
              })}
            >
              <DatePicker allowClear={true} size={"middle"} format={"DD/MM/YYYY"} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name={["rosterConfiguration", "windowCloseDate"]}
              label={formatMessage({
                id: "season.window.close.date",
                defaultMessage: "Window Close Date",
              })}
              tooltip={formatMessage({
                id: "form.help.season.window.close.date",
                defaultMessage: "What is the last date a Season Roster can be submitted from for this Season?",
              })}
            >
              <DatePicker allowClear={true} size={"middle"} format={"DD/MM/YYYY"} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name={["rosterConfiguration", "seasonRosterLock"]}
              label={formatMessage({
                id: "season.roster.lock",
                defaultMessage: "Lock Season Roster?",
              })}
              tooltip={formatMessage({
                id: "form.help.season.roster.lock",
                defaultMessage:
                  "Make Season Rosters un-editable when Season Roster Registration Window Close Date has elapsed.",
              })}
            >
              <Select>
                <Select.Option value={true}>Yes</Select.Option>
                <Select.Option value={false}>No</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name={["rosterConfiguration", "fixtureRosterLock"]}
              label={formatMessage({
                id: "season.match.roster.lock",
                defaultMessage: "Lock Match Rosters?",
              })}
              tooltip={formatMessage({
                id: "form.help.season.match.roster.lock",
                defaultMessage: "Make Match Rosters un-editable when Match Status = Scheduled**",
              })}
            >
              <Select>
                <Select.Option value={true}>Yes</Select.Option>
                <Select.Option value={false}>No</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </PermissionCheck>
      </Form>
      <Row justify={"center"}>
        <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
          <Button loading={isCreatePending} type={"primary"} size={"large"} onClick={handleSend}>
            {formatMessage({ id: "add", defaultMessage: "Add" })}
          </Button>
        </PermissionCheck>
      </Row>
    </Space>
  );
};

export default NewSeasonForm;
